import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import { useNavigate } from 'react-router-dom';
import { LuSearch, LuEye, LuChevronLeft, LuChevronRight, LuMail, LuMessageSquare } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import BrikAgente from '../components/BrikAgente';
import AssistenteMarkdown from '../components/AssistenteMarkdown';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import { ListRow, PerPage, RowAction, RowActions, ListCard, ListCards, FiltroPill } from '../components/ListKit';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
import {
    SugestoesService, TIPOS_NA_LISTA, ORIGENS_SUGESTAO, rotuloTipoSugestao, type SugestaoSistema,
} from '../services/SugestoesService';
import './SugestoesSistemaPage.css';

const POR_PAGINA = [25, 50, 100];
const NOVA = 'Não lidas';
const LIDA = 'Lidas';

const dataHora = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

/**
 * Sistema › Sugestões: o que os usuários mandaram pela caixinha "Suporte e sugestões"
 * da sidebar e pelo cartão "Avisar a equipe Brik" do Brik AI, de todas as empresas. Só a
 * equipe Brik vê (empresa 1 + gbcode_admin_sistema).
 *
 * O que veio do Brik AI traz o selo, a pergunta e a resposta dele, e "Ver conversa" abre a
 * conversa em Sistema › Assistente (desenho em docs/propostas/brik-ai/abre-sugestao.html).
 *
 * Abrir uma sugestão já a marca como lida, como num e-mail; o painel tem o caminho de volta.
 * Responder é fora do app: o e-mail de quem mandou está no painel.
 */
const SugestoesSistemaPage = () => {
    const celular = useCelular();
    const navigate = useNavigate();
    const [sugestoes, setSugestoes] = useState<SugestaoSistema[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [recorte, gravarRecorte] = useRecorte('sug:recorte', {
        busca: '', tipo: null as string | null, origem: null as string | null, situacao: null as string | null, pagina: 1, porPagina: 25,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [tipo, setTipo] = useState<string | null>(recorte.tipo);
    const [origem, setOrigem] = useState<string | null>(recorte.origem ?? null);
    const [situacao, setSituacao] = useState<string | null>(recorte.situacao);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ busca, tipo, origem, situacao, pagina, porPagina });
    }, [busca, tipo, origem, situacao, pagina, porPagina, gravarRecorte]);

    const [aberta, setAberta] = useState<SugestaoSistema | null>(null);
    const [salvando, setSalvando] = useState(false);

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const r = await SugestoesService.listar();
            if (r.sucesso) setSugestoes(r.dados || []);
            else aviso.erro(r.mensagem || 'Não foi possível carregar as sugestões.');
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, tipo, origem, situacao, porPagina]);

    const naoLidas = sugestoes.filter(s => !s.lidaEm).length;

    const opcoesTipo = useMemo(() => TIPOS_NA_LISTA
        .map(t => ({ valor: t.rotulo, total: sugestoes.filter(s => s.tipo === t.valor).length }))
        .filter(o => o.total > 0), [sugestoes]);
    const opcoesOrigem = useMemo(() => ORIGENS_SUGESTAO
        .map(o => ({ valor: o.rotulo, total: sugestoes.filter(s => origemDe(s) === o.valor).length }))
        .filter(o => o.total > 0), [sugestoes]);
    const opcoesSituacao = useMemo(() => [
        { valor: NOVA, total: naoLidas },
        { valor: LIDA, total: sugestoes.length - naoLidas },
    ].filter(o => o.total > 0), [sugestoes, naoLidas]);

    const filtradas = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return sugestoes.filter(s => {
            if (tipo && rotuloTipoSugestao(s.tipo) !== tipo) return false;
            if (origem && rotuloOrigem(s) !== origem) return false;
            if (situacao === NOVA && s.lidaEm) return false;
            if (situacao === LIDA && !s.lidaEm) return false;
            if (!t) return true;
            return [s.texto, s.pergunta, s.empresaNome, s.usuarioNome, s.usuarioEmail]
                .some(v => (v || '').toLowerCase().includes(t));
        });
    }, [sugestoes, busca, tipo, origem, situacao]);

    const totalPaginas = Math.max(1, Math.ceil(filtradas.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtradas.slice(inicio, inicio + porPagina);

    /* Troca o estado na lista sem recarregar tudo: a linha muda na hora e o
       painel aberto acompanha. Se a API recusar, volta como estava. */
    const marcar = async (s: SugestaoSistema, lida: boolean, silencioso = false) => {
        const antes = s.lidaEm ?? null;
        const agora = lida ? new Date().toISOString() : null;
        const aplicar = (lidaEm: string | null) => {
            setSugestoes(lista => lista.map(x => (x.id === s.id ? { ...x, lidaEm } : x)));
            setAberta(a => (a && a.id === s.id ? { ...a, lidaEm } : a));
        };
        aplicar(agora);
        setSalvando(true);
        try {
            const r = await SugestoesService.marcarLida(s.id, lida);
            if (!r.sucesso) { aplicar(antes); if (!silencioso) aviso.erro(r.mensagem || 'Não foi possível atualizar.'); }
        } catch {
            aplicar(antes);
            if (!silencioso) aviso.erro(SEM_SERVIDOR);
        } finally {
            setSalvando(false);
        }
    };

    const abrir = (s: SugestaoSistema) => {
        setAberta(s);
        if (!s.lidaEm) marcar(s, true, true);
    };

    /* Tipo com 168px: "Melhoria" e o selo "Brik AI" na mesma linha. */
    const COLUNAS = 'minmax(0, 3fr) 168px minmax(0, 1.3fr) minmax(0, 1.2fr) 140px 92px 60px';

    const situacaoDe = (s: SugestaoSistema) => (s.lidaEm
        ? <span className="lk-muted">Lida</span>
        : <span className="lk-wa sug-nova"><i />Nova</span>);

    const filtrando = !!busca.trim() || !!tipo || !!origem || !!situacao;
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {sugestoes.length === 0 ? 'Nenhuma sugestão ainda' : 'Nada com esse recorte'}
            </div>
            <p className="lk-blank-text">
                {sugestoes.length === 0
                    ? 'Quando alguém mandar uma sugestão pelo botão "Suporte e sugestões" da sidebar, ou aceitar avisar a equipe pelo Brik AI, ela aparece aqui.'
                    : 'Ajuste a busca ou os filtros para ver as outras.'}
            </p>
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, filtradas.length)} de {filtradas.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Sugestões"
                subtitle="O que os usuários mandaram pelo botão Suporte e sugestões e pelo Brik AI, de todas as empresas."
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar no texto, empresa ou pessoa…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    <FiltroPill rotulo="Tipo" valor={tipo} opcoes={opcoesTipo} onChange={setTipo} />
                    <FiltroPill rotulo="Origem" valor={origem} opcoes={opcoesOrigem} onChange={setOrigem} />
                    <FiltroPill rotulo="Situação" valor={situacao} opcoes={opcoesSituacao} onChange={setSituacao} />
                    <span className="lk-count">
                        {filtrando ? `${filtradas.length} de ${sugestoes.length}` : sugestoes.length}
                        {' '}{sugestoes.length === 1 ? 'sugestão' : 'sugestões'}
                        {naoLidas > 0 && ` · ${naoLidas} ${naoLidas === 1 ? 'não lida' : 'não lidas'}`}
                    </span>
                </div>

                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(s => (
                                    <ListCard
                                        key={s.id}
                                        titulo={s.texto}
                                        apoio={`${rotuloTipoSugestao(s.tipo)}${peloBrikAi(s) ? ' · Brik AI' : ''} · ${s.empresaNome} · ${dataHora(s.criadaEm)}`}
                                        estado={situacaoDe(s)}
                                        selected={aberta?.id === s.id}
                                        label={`Abrir sugestão de ${s.usuarioNome}`}
                                        onOpen={() => abrir(s)}
                                    />
                                ))}
                        </ListCards>
                        {!isLoading && filtradas.length === 0 && listaVazia}
                        {!isLoading && filtradas.length > 0 && rodape}
                    </>
                ) : (
                    <div className="lk-table">
                        <div className="lk-scroll">
                            <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                                <div className="lk-head">Sugestão</div>
                                <div className="lk-head">Tipo</div>
                                <div className="lk-head">Empresa</div>
                                <div className="lk-head">Pessoa</div>
                                <div className="lk-head">Recebida em</div>
                                <div className="lk-head">Situação</div>
                                <div className="lk-head lk-head-acts">Ações</div>
                            </div>
                            <div className="lk-body">
                                {isLoading
                                    ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                    : visiveis.map(s => (
                                        <ListRow key={s.id} columns={COLUNAS} selected={aberta?.id === s.id}>
                                            <span className={`lk-cell ${s.lidaEm ? '' : 'lk-strong'}`}><span>{s.texto}</span></span>
                                            <span className="lk-cell"><span>{rotuloTipoSugestao(s.tipo)}{peloBrikAi(s) && <SeloBrikAi />}</span></span>
                                            <span className="lk-cell"><span>{s.empresaNome}</span></span>
                                            <span className="lk-cell lk-muted"><span>{s.usuarioNome}</span></span>
                                            <span className="lk-cell lk-muted"><span>{dataHora(s.criadaEm)}</span></span>
                                            <span className="lk-cell">{situacaoDe(s)}</span>
                                            <RowActions>
                                                <RowAction icon={<LuEye size={14} />} label="Ver sugestão"
                                                    onClick={() => abrir(s)} />
                                            </RowActions>
                                        </ListRow>
                                    ))}
                            </div>
                        </div>

                        {!isLoading && filtradas.length === 0 && listaVazia}
                        {!isLoading && filtradas.length > 0 && rodape}
                    </div>
                )}
            </div>

            <RecordPanel
                open={!!aberta}
                onClose={() => setAberta(null)}
                title={aberta ? `${rotuloTipoSugestao(aberta.tipo)} de ${aberta.usuarioNome}` : ''}
                subtitle={aberta ? `${aberta.empresaNome} · ${dataHora(aberta.criadaEm)}${peloBrikAi(aberta) ? ' · pelo Brik AI' : ''}` : undefined}
                actions={aberta && (<>
                    <a className="rp-btn is-primary sug-responder"
                        href={`mailto:${aberta.usuarioEmail}?subject=${encodeURIComponent('Sua sugestão para o Brik CRM')}`}>
                        <LuMail size={13} /> Responder por e-mail
                    </a>
                    {peloBrikAi(aberta) && aberta.assistenteMensagemId && (
                        <button type="button" className="rp-btn"
                            onClick={() => navigate(`/sistema/assistente?pergunta=${aberta.assistenteMensagemId}`)}>
                            <LuMessageSquare size={13} /> Ver conversa
                        </button>
                    )}
                </>)}
                footer={aberta && (
                    <button type="button" className="rp-btn" disabled={salvando}
                        onClick={() => marcar(aberta, !aberta.lidaEm)}>
                        {aberta.lidaEm ? 'Marcar como não lida' : 'Marcar como lida'}
                    </button>
                )}
            >
                {aberta && (
                    <>
                        <PanelSection title={peloBrikAi(aberta) ? 'O pedido' : 'Mensagem'}>
                            <p className="sug-texto">{aberta.texto}</p>
                        </PanelSection>
                        {peloBrikAi(aberta) && aberta.pergunta && (
                            <PanelSection title="Como a pessoa perguntou">
                                <p className="sug-texto sug-citacao">{aberta.pergunta}</p>
                            </PanelSection>
                        )}
                        {peloBrikAi(aberta) && aberta.resposta && (
                            <PanelSection title="O que o Brik AI respondeu">
                                <div className="sug-citacao sug-resposta"><AssistenteMarkdown texto={aberta.resposta} /></div>
                            </PanelSection>
                        )}
                        <PanelSection title="Quem mandou">
                            <div className="rp-kv">
                                <span className="rp-k">Pessoa</span>
                                <span className="rp-v">{aberta.usuarioNome}</span>
                                <span className="rp-k">E-mail</span>
                                <span className="rp-v">{aberta.usuarioEmail}</span>
                                <span className="rp-k">Empresa</span>
                                <span className="rp-v">{aberta.empresaNome} <span className="lk-muted">#{aberta.empresaId}</span></span>
                            </div>
                        </PanelSection>
                        <PanelSection title="Quando e onde">
                            <div className="rp-kv">
                                <span className="rp-k">Recebida em</span>
                                <span className="rp-v">{dataHora(aberta.criadaEm)}</span>
                                <span className="rp-k">Na tela</span>
                                {aberta.tela
                                    ? <span className="rp-v"><code className="sug-tela">{aberta.tela}</code></span>
                                    : <span className="rp-v is-empty">Não informada</span>}
                                <span className="rp-k">Situação</span>
                                <span className="rp-v">{aberta.lidaEm ? `Lida em ${dataHora(aberta.lidaEm)}` : 'Não lida'}</span>
                            </div>
                        </PanelSection>
                    </>
                )}
            </RecordPanel>
        </div>
    );
};

const origemDe = (s: SugestaoSistema) => s.origem ?? 'caixinha';
const peloBrikAi = (s: SugestaoSistema) => origemDe(s) === 'brik_ai';
const rotuloOrigem = (s: SugestaoSistema) => ORIGENS_SUGESTAO.find(o => o.valor === origemDe(s))?.rotulo ?? '';

/** O selo da coluna Tipo: o tijolinho do Brik AI, parado, e o nome. */
const SeloBrikAi = () => (
    <span className="sug-selo"><BrikAgente tamanho="pequeno" justo px={10} animar={false} /> Brik AI</span>
);

export default SugestoesSistemaPage;
